import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { demoRates } from '../data/demo';
import { formatMoneyRaw, setDisplayRates } from '../lib/format';
import type { Moneda, Tasa } from '../api/types';

type Currency = Moneda;

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  rates: Tasa[];
  usdRate: number;
  eurRate: number;
  convertFromUsd: (valueUsd: number) => number;
  formatMoney: (valueUsd: number) => string;
  formatCompact: (valueUsd: number) => string;
  rateFor: (currency: Moneda) => number;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);
const KEY = 'gravenca.currency';

export function CurrencyProvider({
  children,
  rates: providedRates,
}: {
  children: ReactNode;
  rates: Tasa[];
}) {
  // La operación completa se lleva en bolívares. El USD se conserva como
  // respaldo contable y para reportes, pero no se ofrece como vista alternativa.
  const [currency, setCurrencyState] = useState<Currency>('VES');

  const rates = providedRates.length > 0 ? providedRates : demoRates;

  const usdRate = useMemo(
    () => rates.find((rate) => rate.moneda === 'USD')?.tasa ?? 36.42,
    [rates],
  );
  const eurRate = useMemo(
    () => rates.find((rate) => rate.moneda === 'EUR')?.tasa ?? 39.87,
    [rates],
  );

  setDisplayRates(usdRate, eurRate);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    window.localStorage.setItem(KEY, next);
  }, []);

  const convertFromUsd = useCallback(
    (valueUsd: number) => valueUsd * usdRate,
    [usdRate],
  );

  const formatMoney = useCallback(
    (valueUsd: number) => formatMoneyRaw(convertFromUsd(valueUsd), 'VES'),
    [convertFromUsd],
  );

  const formatCompact = useCallback(
    (valueUsd: number) => {
      const converted = convertFromUsd(valueUsd);
      if (Math.abs(converted) < 1000) return formatMoneyRaw(converted, 'VES');
      const compact = new Intl.NumberFormat('es-VE', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(converted);
      return `Bs. ${compact}`;
    },
    [convertFromUsd],
  );

  const rateFor = useCallback(
    (target: Moneda) => {
      if (target === 'USD') return usdRate;
      if (target === 'EUR') return eurRate;
      return usdRate;
    },
    [usdRate, eurRate],
  );

  useEffect(() => {
    window.localStorage.setItem(KEY, 'VES');
  }, []);

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      rates,
      usdRate,
      eurRate,
      convertFromUsd,
      formatMoney,
      formatCompact,
      rateFor,
    }),
    [
      currency,
      setCurrency,
      rates,
      usdRate,
      eurRate,
      convertFromUsd,
      formatMoney,
      formatCompact,
      rateFor,
    ],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency debe usarse dentro de CurrencyProvider');
  return context;
}
